import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { FeatureFlags, LoginType, Permission } from '../enums/generated.enums';
import { LoggedUser } from '../types/logged-user.type';
import { LoggingService } from './logging.service';
import { MsalService } from './msal.service';
import { StorageService } from './storage.service';
import { ApiService } from './api/api.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _user: LoggedUser;
  private refreshTokenTimeoutId: ReturnType<typeof setTimeout>;
  private refreshTokenRequest: Promise<boolean>;

  get user(): LoggedUser {
    return this._user;
  }

  get currentOrganization(): string {
    return this._user?.organization ?? null;
  }

  get isLoggedIn(): boolean {
    return this._user != null;
  }

  get isSystemAdmin(): boolean {
    return this.hasPermission(Permission.SystemAdmin);
  }

  private get loginType(): LoginType {
    return this.storageService.loginType ?? LoginType.Password;
  }

  constructor(
    private loggingService: LoggingService,
    private apiService: ApiService,
    private msalService: MsalService,
    private storageService: StorageService,
  ) {
  }

  async initializeUser(force = false): Promise<void> {
    if (this.isLoggedIn && !force) {
      return;
    }

    try {
      this._user = await this.apiService.auth.getLoggedUser();
      this.loggingService.setAuthenticatedUser(this._user.id, this._user.organization, this.isSystemAdmin);
      this.startTokenRefresh();
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
        await this.refreshToken();
        return;
      }
      this.clearUser();
    }
  }

  private clearUser(): void {
    this._user = null;
    this.loggingService.clearAuthenticatedUser();
    this.stopTokenRefresh();
  }

  async loginWithPassword(email: string, password: string): Promise<void> {
    try {
      await this.apiService.auth.login(email, password);
      this.storageService.loginType = LoginType.Password;
    } catch (error) {
      this.loggingService.logError('Failed to login', error);
      throw error;
    } finally {
      await this.initializeUser(true);
    }
  }

  async loginWithMicrosoft(): Promise<void> {
    try {
      const token = await this.msalService.login();
      if (token != null) {
        await this.apiService.auth.loginWithMicrosoft(token);
        this.storageService.loginType = LoginType.Microsoft;
      }
    } catch (error) {
      this.loggingService.logError('Failed to login with Microsoft', error);
      throw error;
    } finally {
      await this.initializeUser(true);
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.loginType === LoginType.Microsoft) {
        await this.msalService.logout();
      }
      await this.apiService.auth.logout();
    } finally {
      this.clearUser();
    }
  }

  async refreshToken(): Promise<boolean> {
    if (this.refreshTokenRequest == null) {
      try {
        this.refreshTokenRequest = this.refreshTokenInternal();
        return await this.refreshTokenRequest;
      } catch (error) {
        this.loggingService.logError('Failed to refresh token', error);
        return false;
      } finally {
        this.refreshTokenRequest = null;
      }
    }

    return await this.refreshTokenRequest;
  }

  private async refreshTokenInternal(): Promise<boolean> {
    if (this.loginType === LoginType.Microsoft) {
      const token = await this.msalService.acquireToken();
      await this.apiService.auth.loginWithMicrosoft(token);
    } else {
      await this.apiService.auth.refreshToken();
    }

    await this.initializeUser(true);
    return true;
  }

  async setOrganization(organizationName: string): Promise<void> {
    try {
      if (this.loginType === LoginType.Microsoft) {
        const token = await this.msalService.acquireToken();
        await this.apiService.auth.loginWithMicrosoft(token, organizationName);
      } else {
        await this.apiService.auth.setOrganization(organizationName);
      }
      await this.refreshToken();
    } catch (error) {
      this.loggingService.logError('Failed to set organization', error);
      throw error;
    }
  }

  hasPermission(permission: Permission): boolean {
    if (permission == null) return false;
    return this._user?.permissions?.includes(permission) ?? false;
  }

  hasAnyPermission(permissions: Permission[]): boolean {
    if (permissions == null) return false;
    return this._user?.permissions?.some(p => permissions.includes(p)) ?? false;
  }

  hasFeatureFlag(featureFlag: FeatureFlags): boolean {
    if (featureFlag == null) return false;
    return this._user?.featureFlags?.includes(featureFlag) ?? false;
  }

  private startTokenRefresh(): void {
    this.stopTokenRefresh();
    this.refreshTokenTimeoutId = setTimeout(() => this.refreshToken(), 600000); // 10 minutes
  }

  private stopTokenRefresh(): void {
    if (this.refreshTokenTimeoutId) {
      clearTimeout(this.refreshTokenTimeoutId);
      this.refreshTokenTimeoutId = null;
    }
  }
}
